import BottomFixedCallToAction from "@/features/Common/components/BottomFixedCallToAction";
import Panel from "@/features/Common/components/Panel";
import theme, {
  defaultFullscreenPageStyling,
  styleInputDefault,
} from "@/theme";
import { convertPxToRem } from "@/utils";
import {
  InputLabel,
  Stack,
  Typography,
  TextField,
  Divider,
  Box,
} from "@mui/material";
import { useTranslation, Trans } from "react-i18next";
import IconLoader from "@/features/Common/components/IconLoader";
import InternationalPhoneNumberInput from "@/features/Common/components/InternationalPhoneNumberInput";
import ButtonWithAnalytics from "@/features/Common/components/ButtonWithAnalytics";
import { useNavigate } from "react-router-dom";
import GetSupportPanel from "@/features/Common/components/GetSupportPanel";

export default function DocMedsConsultationComplete() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const deliveryAddress = "4 Bath Ave, Rosebank, Johannesburg";
  const patientName = "Moses Tshabalala";
  const isOutOfHours = true;

  function onConfirmButtonClick() {
    console.log("Confirmation button clicked");
  }

  return (
    <Stack
      spacing={3}
      minHeight="100vh"
      position="relative"
      sx={{
        ...defaultFullscreenPageStyling,
        paddingBottom: convertPxToRem(130),
      }}
    >
      <Stack spacing={1}>
        <Typography variant="h2" component="h1">
          {t("DocMedsConsultationComplete.title")}
        </Typography>
        <Typography variant="body1" component="p" color="neutral.main">
          {t("DocMedsConsultationComplete.subtitle")}
        </Typography>
      </Stack>

      <Panel sx={{ boxShadow: "none" }}>
        <Stack direction="row" gap={2}>
          <IconLoader
            icon="InformationSymbolFilledCircleIcon"
            color="primary"
            sx={{ fontSize: convertPxToRem(30) }}
          />
          <Typography variant="body2">
            {t("DocMedsConsultationComplete.deliveryPartnerInfo")}
          </Typography>
        </Stack>
      </Panel>

      <Panel>
        <Stack spacing={2}>
          {isOutOfHours && (
            <Box
              sx={{
                padding: 2,
                borderRadius: 1,
                backgroundColor: (theme.palette.accent4 as any).lightest,
                display: "flex",
                flexDirection: "row",
                gap: 2,
              }}
            >
              <IconLoader
                icon="WarningIcon"
                color="error"
                sx={{ fontSize: convertPxToRem(24) }}
              />
              <Stack>
                <Typography variant="body2" fontWeight="bold">
                  {t("DocMedsConsultationComplete.deliveryCutOff.title")}
                </Typography>
                <Typography fontSize={convertPxToRem(12)}>
                  {t("DocMedsConsultationComplete.deliveryCutOff.subtitle")}
                </Typography>
              </Stack>
            </Box>
          )}
          <Stack spacing={1}>
            <Typography variant="h3" component="h2" fontWeight="bold">
              {t("DocMedsConsultationComplete.deliveryDetail.title")}
            </Typography>
            <Typography variant="body1" color="neutral.main">
              {t("DocMedsConsultationComplete.deliveryDetail.subtitle")}
            </Typography>
          </Stack>
          <Stack direction="row" gap={1}>
            <IconLoader
              icon="PhoneIcon"
              sx={{ fontSize: convertPxToRem(20) }}
            />
            <Stack spacing={2} width="100%">
              <Stack spacing={1}>
                <Typography
                  variant="body2"
                  color="neutral.main"
                  fontWeight="bold"
                >
                  {t(
                    "DocMedsConsultationComplete.deliveryDetail.contactPerson.title"
                  )}
                </Typography>
                <Typography
                  variant="body1"
                  color="neutral.main"
                  sx={{ fontSize: convertPxToRem(14) }}
                >
                  {t(
                    "DocMedsConsultationComplete.deliveryDetail.contactPerson.subtitle"
                  )}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                <InputLabel>
                  <Typography
                    variant="body2"
                    color="neutral.main"
                    fontWeight="bold"
                  >
                    {t(
                      "DocMedsConsultationComplete.deliveryDetail.contactPerson.label"
                    )}
                  </Typography>
                </InputLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  size="small"
                  sx={{
                    input: {
                      ...styleInputDefault,
                      fontSize: convertPxToRem(16),
                    },
                  }}
                />
              </Stack>

              <Stack spacing={1}>
                <InputLabel>
                  <Typography
                    variant="body2"
                    color="neutral.main"
                    fontWeight="bold"
                  >
                    {t(
                      "DocMedsConsultationComplete.deliveryDetail.contactNumber.label"
                    )}
                  </Typography>
                </InputLabel>
                <InternationalPhoneNumberInput
                  onChange={() => {}}
                  value={{
                    countryCode: "ZA",
                    globalSubscriberNumber: "",
                  }}
                  size="small"
                  inputStylesOverride={styleInputDefault}
                />
              </Stack>
            </Stack>
          </Stack>
          <Divider />
          <Stack direction="row" gap={1}>
            <IconLoader
              icon="PageWithPencilIcon"
              sx={{ fontSize: convertPxToRem(20) }}
            />
            <Stack spacing={2} width="100%">
              <Stack spacing={1}>
                <Typography
                  variant="body2"
                  color="neutral.main"
                  fontWeight="bold"
                >
                  {t(
                    "DocMedsConsultationComplete.deliveryDetail.deliveryNotes.title"
                  )}
                </Typography>
                <Typography
                  variant="body1"
                  color="neutral.main"
                  sx={{ fontSize: convertPxToRem(14) }}
                >
                  {t(
                    "DocMedsConsultationComplete.deliveryDetail.deliveryNotes.subtitle"
                  )}
                </Typography>
              </Stack>

              <Stack spacing={1}>
                <InputLabel>
                  <Typography
                    variant="body2"
                    color="neutral.main"
                    fontWeight="bold"
                  >
                    {t(
                      "DocMedsConsultationComplete.deliveryDetail.deliveryNotes.title"
                    )}
                  </Typography>
                </InputLabel>
                <TextField
                  variant="outlined"
                  fullWidth
                  multiline
                  minRows={3}
                  maxRows={6}
                  size="small"
                  placeholder={t(
                    "DocMedsConsultationComplete.deliveryDetail.deliveryNotes.placeholder" as any
                  )}
                  sx={{
                    input: {
                      ...styleInputDefault,
                      fontSize: convertPxToRem(16),
                    },
                  }}
                />
              </Stack>
            </Stack>
          </Stack>
          <Divider />
          <Stack direction="row" gap={1}>
            <IconLoader icon="UserIcon" sx={{ fontSize: convertPxToRem(20) }} />
            <Stack>
              <Typography
                variant="body2"
                color="neutral.main"
                fontWeight="bold"
              >
                {t(
                  "DocMedsConsultationComplete.deliveryDetail.deliveryNotes.patient"
                )}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {patientName}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Stack direction="row" gap={1}>
            <IconLoader
              icon="MapMarkerIcon"
              sx={{ fontSize: convertPxToRem(20) }}
            />
            <Stack>
              <Typography
                variant="body2"
                color="neutral.main"
                fontWeight="bold"
              >
                {t(
                  "DocMedsConsultationComplete.deliveryDetail.deliveryNotes.deliveryAddress"
                )}
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {deliveryAddress}
              </Typography>
            </Stack>
          </Stack>
        </Stack>
      </Panel>

      <GetSupportPanel analyticsPage="Doc Meds Consultation Complete" />

      <BottomFixedCallToAction
        buttonText="common.confirm"
        onButtonClick={onConfirmButtonClick}
        ariaLabel="common.confirm"
        analyticsPageName="Doc Meds Consultation Complete"
      />
    </Stack>
  );
}
